export type Order = 'asc' | 'desc';

export function normalizeName(name: string): string {
  return name.replace(/^[\s\d]+/, '').toLocaleLowerCase();
}

/**
 * Retrieves the value of a nested property from an object using a dot-separated path.
 * @param obj The object to retrieve the value from.
 * @param path The dot-separated path to the nested property.
 * @returns The value of the nested property, or undefined if any part of the path is undefined.
 */
function getNestedValue(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce((value, key) => value?.[key], obj);
}

/**
 * Compares two values for sorting in descending order.
 * @param a The first value to compare.
 * @param b The second value to compare.
 * @param orderBy The key or nested path to use for comparison.
 * @returns A negative number if b should come before a, a positive number if b should come after a, or 0 if they are considered equal.
 */
export function descendingComparator<T>(a: T, b: T, orderBy: string) {
  const valA = getNestedValue(a as Record<string, any>, orderBy);
  const valB = getNestedValue(b as Record<string, any>, orderBy);

  if (valB === null || valB === undefined) return -1;
  if (valA === null || valA === undefined) return 1;

  if (typeof valA === 'string' && typeof valB === 'string') {
    return valB.localeCompare(valA, undefined, { numeric: true });
  }

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }
  return 0;
}

/**
 * Returns a comparator function for sorting based on the specified order and key.
 * @param order The sort order, either 'asc' for ascending or 'desc' for descending.
 * @param orderBy The key or nested path to use for comparison.
 * @returns A comparator function that can be used with Array.prototype.sort.
 */
export function getComparator<T = any>(order: Order, orderBy: string): (a: T, b: T) => number {
  return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Performs a stable sort on an array using the provided comparator function.
 * @param array The array to sort.
 * @param comparator The comparator function to determine the order of elements.
 * @returns A new array sorted in a stable manner.
 */
export function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);

    if (order !== 0) return order;

    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}
